import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReactFlow, {
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { autoLayout, needsAutoLayout } from "@/lib/autoLayout";

// Components
import Header from "@/components/Header";
import RoleNode from "@/components/map/RoleNode";
import DepartmentNode from "@/components/map/DepartmentNode";
import ProcessNode from "@/components/map/ProcessNode";
import SystemNode from "@/components/map/SystemNode";
import DetailSheet from "@/components/panels/DetailSheet";
import SearchCommand from "@/components/SearchCommand";
import TrainingTour, { TourSelector } from "@/components/TrainingTour";

// View modes
const VIEW_MODES = {
  ORG: "org",
  FUNCTION: "function",
  PROCESS: "process",
  SYSTEM: "system",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  
  // State
  const [viewMode, setViewMode] = useState(VIEW_MODES.ORG);
  const [trainingMode, setTrainingMode] = useState(false);
  const [presentationMode, setPresentationMode] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [tourSelectorOpen, setTourSelectorOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [selectedTour, setSelectedTour] = useState("overview");
  
  // Data
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [systems, setSystems] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  // React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Custom node types
  const nodeTypes = useMemo(() => ({
    roleNode: RoleNode,
    departmentNode: DepartmentNode,
    processNode: ProcessNode,
    systemNode: SystemNode,
  }), []);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, deptsRes, procsRes, sysRes, connsRes] = await Promise.all([
        axios.get(`${API}/roles`, { withCredentials: true }),
        axios.get(`${API}/departments`, { withCredentials: true }),
        axios.get(`${API}/processes`, { withCredentials: true }),
        axios.get(`${API}/systems`, { withCredentials: true }),
        axios.get(`${API}/connections`, { withCredentials: true }),
      ]);
      
      setRoles(rolesRes.data);
      setDepartments(deptsRes.data);
      setProcesses(procsRes.data);
      setSystems(sysRes.data);
      setConnections(connsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        logout();
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Generate nodes and edges based on view mode
  useEffect(() => {
    if (loading) return;

    let newNodes = [];
    let newEdges = [];

    switch (viewMode) {
      case VIEW_MODES.ORG:
        // Roles organized by department
        newNodes = roles.map((role) => ({
          id: role.role_id,
          type: "roleNode",
          position: { x: role.position_x || 0, y: role.position_y || 0 },
          data: { 
            ...role, 
            onSelect: () => handleNodeSelect("role", role),
            trainingMode,
            presentationMode,
          },
        }));
        
        // Add reporting relationships
        newEdges = connections
          .filter((c) => c.source_type === "role" && c.target_type === "role")
          .map((conn) => ({
            id: conn.connection_id,
            source: conn.source_id,
            target: conn.target_id,
            type: "smoothstep",
            animated: trainingMode,
            label: conn.label,
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { stroke: "#6B7280", strokeWidth: 2 },
          }));
        break;

      case VIEW_MODES.FUNCTION:
        // Departments view
        newNodes = departments.map((dept) => ({
          id: dept.department_id,
          type: "departmentNode",
          position: { x: dept.position_x || 0, y: dept.position_y || 0 },
          data: { 
            ...dept, 
            onSelect: () => handleNodeSelect("department", dept),
            trainingMode,
            presentationMode,
          },
        }));
        
        // Department handoffs
        newEdges = connections
          .filter((c) => c.source_type === "department" && c.target_type === "department")
          .map((conn) => ({
            id: conn.connection_id,
            source: conn.source_id,
            target: conn.target_id,
            type: "smoothstep",
            animated: trainingMode,
            label: conn.label,
            labelStyle: { fontSize: 11, fill: "#6B7280" },
            labelBgStyle: { fill: "white", fillOpacity: 0.9 },
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { stroke: "#6B7280", strokeWidth: 2 },
          }));
        break;

      case VIEW_MODES.PROCESS:
        // Processes view
        newNodes = processes.map((proc) => ({
          id: proc.process_id,
          type: "processNode",
          position: { x: proc.position_x || 0, y: proc.position_y || 0 },
          data: { 
            ...proc, 
            onSelect: () => handleNodeSelect("process", proc),
            trainingMode,
            presentationMode,
          },
        }));
        
        // Process dependencies
        newEdges = processes
          .filter((p) => p.dependencies?.length > 0)
          .flatMap((proc) =>
            proc.dependencies.map((depId, idx) => ({
              id: `${proc.process_id}-dep-${idx}`,
              source: depId,
              target: proc.process_id,
              type: "smoothstep",
              animated: trainingMode,
              label: "depends on",
              markerEnd: { type: MarkerType.ArrowClosed },
              style: { stroke: "#6B7280", strokeWidth: 2, strokeDasharray: "5 5" },
            }))
          );
        break;

      case VIEW_MODES.SYSTEM:
        // Systems view
        newNodes = systems.map((sys) => ({
          id: sys.system_id,
          type: "systemNode",
          position: { x: sys.position_x || 0, y: sys.position_y || 0 },
          data: { 
            ...sys, 
            onSelect: () => handleNodeSelect("system", sys),
            trainingMode,
            presentationMode,
          },
        }));
        break;

      default:
        break;
    }

    setNodes(newNodes);
    setEdges(newEdges);
  }, [viewMode, roles, departments, processes, systems, connections, loading, trainingMode, presentationMode, setNodes, setEdges]);

  // Direction per view mode for auto-layout
  const layoutDirection = useMemo(() => {
    return viewMode === VIEW_MODES.ORG || viewMode === VIEW_MODES.SYSTEM ? "TB" : "LR";
  }, [viewMode]);

  // Auto-apply a layout when all current nodes are stacked at (0,0)
  useEffect(() => {
    if (loading) return;
    if (nodes.length === 0) return;
    if (!needsAutoLayout(nodes)) return;
    const { nodes: lnodes, edges: ledges } = autoLayout(nodes, edges, layoutDirection);
    setNodes(lnodes);
    setEdges(ledges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes.length, layoutDirection]);

  // Persist auto-layout positions to backend
  const persistPositions = useCallback(async (laidOutNodes) => {
    const typeMap = {
      roleNode: "role",
      departmentNode: "department",
      processNode: "process",
      systemNode: "system",
    };
    const positions = laidOutNodes
      .filter((n) => typeMap[n.type])
      .map((n) => ({
        id: n.id,
        type: typeMap[n.type],
        position_x: n.position.x,
        position_y: n.position.y,
      }));
    if (positions.length === 0) return;
    try {
      await axios.patch(`${API}/positions/batch`, { positions }, { withCredentials: true });
    } catch (err) {
      console.error("Failed to save layout positions:", err);
    }
  }, []);

  const handleAutoLayout = useCallback(async () => {
    if (nodes.length === 0) {
      toast.info("Nothing to arrange — add some entities first.");
      return;
    }
    const { nodes: lnodes, edges: ledges } = autoLayout(nodes, edges, layoutDirection);
    setNodes(lnodes);
    setEdges(ledges);
    if (isAdmin) {
      await persistPositions(lnodes);
      toast.success("Layout rearranged and saved");
    } else {
      toast.success("Layout rearranged (positions are not saved — admins only)");
    }
  }, [nodes, edges, layoutDirection, isAdmin, persistPositions, setNodes, setEdges]);

  // Handle node selection
  const handleNodeSelect = (type, item) => {
    setSelectedItem({ type, data: item });
    setSheetOpen(true);
  };

  // Handle search result selection
  const handleSearchSelect = (result) => {
    const typeMap = {
      role: { data: roles, idField: "role_id" },
      department: { data: departments, idField: "department_id" },
      process: { data: processes, idField: "process_id" },
      system: { data: systems, idField: "system_id" },
    };

    const config = typeMap[result.type];
    if (config) {
      const item = config.data.find((d) => d[config.idField] === result.id);
      if (item) {
        // Switch to appropriate view mode
        const viewModeMap = {
          role: VIEW_MODES.ORG,
          department: VIEW_MODES.FUNCTION,
          process: VIEW_MODES.PROCESS,
          system: VIEW_MODES.SYSTEM,
        };
        setViewMode(viewModeMap[result.type]);
        handleNodeSelect(result.type, item);
      }
    }
    setSearchOpen(false);
  };

  // Handle node drag end - save positions
  const onNodeDragStop = useCallback(async (event, node) => {
    if (!isAdmin) return;
    
    try {
      const typeMap = {
        roleNode: "role",
        departmentNode: "department",
        processNode: "process",
        systemNode: "system",
      };
      
      await axios.patch(`${API}/positions/batch`, {
        positions: [{
          id: node.id,
          type: typeMap[node.type],
          position_x: node.position.x,
          position_y: node.position.y,
        }]
      }, { withCredentials: true });
    } catch (error) {
      console.error("Failed to save position:", error);
    }
  }, [isAdmin]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") {
        setSheetOpen(false);
        setSearchOpen(false);
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading organization data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen flex flex-col bg-background ${presentationMode ? 'presentation-mode' : ''}`} data-testid="dashboard">
      <Header
        user={user}
        onLogout={logout}
        viewMode={viewMode}
        setViewMode={setViewMode}
        trainingMode={trainingMode}
        setTrainingMode={setTrainingMode}
        presentationMode={presentationMode}
        setPresentationMode={setPresentationMode}
        onSearchOpen={() => setSearchOpen(true)}
        isAdmin={isAdmin}
        onAdminClick={() => navigate("/admin")}
        onStartTour={() => setTourSelectorOpen(true)}
      />

      <div className="flex-1 relative">
        {/* Auto-layout floating button */}
        <div className="absolute top-4 right-4 z-10 flex gap-2">
          <button
            onClick={handleAutoLayout}
            className="bg-white hover:bg-primary hover:text-white border border-border text-foreground text-sm font-medium px-3 py-2 rounded-md shadow-md transition-colors flex items-center gap-2"
            data-testid="auto-layout-btn"
            title={`Auto-arrange nodes (${layoutDirection === "TB" ? "top-to-bottom" : "left-to-right"})`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="6" height="6" rx="1"/>
              <rect x="15" y="3" width="6" height="6" rx="1"/>
              <rect x="9" y="15" width="6" height="6" rx="1"/>
              <path d="M6 9v3a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V9"/>
            </svg>
            Auto-Layout
          </button>
        </div>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDragStop={onNodeDragStop}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.3}
          maxZoom={2}
          nodesDraggable={isAdmin}
          className="bg-secondary/30"
          data-testid="react-flow-canvas"
        >
          <Controls className="!shadow-lg !border !border-border" />
          <MiniMap
            nodeStrokeWidth={3}
            nodeColor={(node) => {
              if (node.type === "roleNode") return "#002FA7";
              if (node.type === "departmentNode") return node.data.color || "#059669";
              if (node.type === "processNode") return "#7C3AED";
              return "#DC2626";
            }}
            className="!bg-white"
          />
          <Background color="#E5E7EB" gap={20} />
        </ReactFlow>

        {/* Empty state */}
        {nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center p-8 bg-white rounded-md shadow-lg pointer-events-auto">
              <p className="text-lg font-semibold text-foreground mb-2">No data available</p>
              <p className="text-muted-foreground mb-4">
                {isAdmin ? "Start by seeding the initial data from the admin panel." : "Contact an admin to set up the organization data."}
              </p>
              {isAdmin && (
                <button
                  onClick={() => navigate("/admin")}
                  className="text-primary font-medium hover:underline"
                  data-testid="go-to-admin-btn"
                >
                  Go to Admin Panel →
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Detail Sheet */}
      <DetailSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        item={selectedItem}
        roles={roles}
        departments={departments}
        systems={systems}
      />

      {/* Search Command */}
      <SearchCommand
        open={searchOpen}
        onOpenChange={setSearchOpen}
        onSelect={handleSearchSelect}
      />

      {/* Tour Selector */}
      <TourSelector
        open={tourSelectorOpen}
        onOpenChange={setTourSelectorOpen}
        onSelectTour={(tourId) => {
          setSelectedTour(tourId);
          setTourSelectorOpen(false);
          setTourOpen(true);
        }}
      />

      {/* Training Tour */}
      <TrainingTour
        open={tourOpen}
        onOpenChange={setTourOpen}
        tourType={selectedTour}
        onSwitchView={(view) => setViewMode(view)}
        onFocusNode={(nodeId) => {
          // Focus on specific node - could zoom/center on it
          console.log("Focus on node:", nodeId);
        }}
      />
    </div>
  );
}
